import Editor, { type OnMount } from '@monaco-editor/react'
import type { editor as MonacoEditor } from 'monaco-editor'
import { useEffect, useRef } from 'react'

import type { CodeProblem } from '~/lib/code-problems'
import { LANGUAGE_ID, registerMixedScript, setProblemMarker } from '~/lib/mixedscript-monaco'

/**
 * The code surface both script editors share — the whole-script drawer and a dynamic block's
 * editor. Same Monaco, same MixedScript colours, same problem squiggles; only the size and
 * what the text is wrapped in differ.
 *
 * The editor owns its text. A controlled Monaco resets its content whenever the prop lags the
 * keystrokes — one slow render and the cursor lands at the end of the file — so the value prop
 * is only ever pushed into the model when it is not this editor's own typing coming home.
 */
export function CodeEditor({
  label,
  value,
  onChange,
  problem,
  height,
  rounded = 'rounded-2xl',
}: {
  label: string
  value: string
  onChange: (next: string) => void
  /** Where the text goes wrong, or null while it parses. */
  problem: CodeProblem | null
  /** A fixed height for an inline editor; the drawer stretches with `100%`. */
  height: string
  /** How the box is cut. An editor meeting something above it squares its top corners. */
  rounded?: string
}) {
  registerMixedScript()
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null)
  // What this editor has reported, oldest first, until the value prop catches up with it.
  const pending = useRef<string[]>([])

  useEffect(() => {
    const editor = editorRef.current
    if (editor === null) {
      return
    }
    const echoed = pending.current.indexOf(value)
    if (echoed !== -1) {
      // Our own text, possibly a few keystrokes behind what is already on screen. Touching
      // the model now would throw the cursor; the newer text is on its way here anyway.
      pending.current.splice(0, echoed + 1)
      return
    }
    pending.current = []
    const model = editor.getModel()
    if (model && model.getValue() !== value) {
      // An outside change — undo, a board edit. Replacing as an edit keeps the cursor near.
      editor.executeEdits('outside', [{ range: model.getFullModelRange(), text: value }])
    }
  }, [value])

  useEffect(() => {
    const model = editorRef.current?.getModel()
    if (model) {
      setProblemMarker(model, problem)
    }
  }, [problem])

  const onMount: OnMount = (mounted) => {
    editorRef.current = mounted
    const model = mounted.getModel()
    if (model) {
      setProblemMarker(model, problem)
    }
  }

  return (
    <div aria-label={label} className={`overflow-hidden ${rounded}`} style={{ height }}>
      <Editor
        language={LANGUAGE_ID}
        theme="mixedscript-dark"
        defaultValue={value}
        onChange={(next) => {
          const text = next ?? ''
          pending.current.push(text)
          onChange(text)
        }}
        onMount={onMount}
        options={{
          fontSize: 13,
          lineHeight: 22,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, 'Cascadia Code', monospace",
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'off',
          tabSize: 2,
          renderLineHighlight: 'line',
          lineNumbers: 'on',
          glyphMargin: false,
          folding: true,
          bracketPairColorization: { enabled: true },
          automaticLayout: true,
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          padding: { top: 12, bottom: 12 },
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          overviewRulerBorder: false,
          scrollbar: { vertical: 'auto', horizontal: 'auto', verticalScrollbarSize: 8 },
        }}
      />
    </div>
  )
}
